import { cn } from "@/lib/utils";
import queries from "@/queries";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useLayoutEffect, useRef, useState } from "react";

// 카드 그리드 미리보기(mini)의 고정 배율. scale 과 width(=100/scale%)가 연동된다.
const MINI_SCALE = 0.55;

/**
 * 백엔드 dsd-view HTML로 표를 렌더링한다.
 * mini면 고정 배율(MINI_SCALE)로 축소된 카드 미리보기(클릭 불가),
 * 아니면 컨테이너 너비에 맞춰 자동 축소(fit-to-width)하고 scaleFactor 로 추가 축소한다.
 */
export const TableHtmlPreview = ({
  workbookId,
  tableId,
  mini = false,
  scaleFactor = 1,
  className,
}: {
  workbookId: string;
  tableId: string;
  mini?: boolean;
  scaleFactor?: number;
  className?: string;
}) => {
  const { data, isLoading } = useQuery({
    ...queries.workbook(workbookId)._ctx.tables(tableId)._ctx.dsdView(),
    enabled: !!workbookId && !!tableId,
  });
  const html = data?.html?.replaceAll("#FEFCE8", "#FFFFFF") || "";

  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(scaleFactor);
  const [fitWidthScale, setFitWidthScale] = useState(1);
  const [scaledHeight, setScaledHeight] = useState<number>();

  // 표 HTML의 원본 너비를 재서 컨테이너에 맞는 배율을 계산한다. transform 은
  // 레이아웃 높이에 반영되지 않으므로 축소된 높이를 wrapper 에 직접 지정한다.
  useLayoutEffect(() => {
    if (mini) return;
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    const measure = () => {
      const contentWidth = content.scrollWidth;
      const containerWidth = container.clientWidth;
      if (!contentWidth || !containerWidth) return;
      // fit: 표를 컨테이너 너비에 맞추는 배율(표가 넘칠 때만 <1). 표를 원본 폭으로
      // 펼친 뒤 축소하기 위해 width 계산에만 쓰고, 시각 배율은 fit*scaleFactor 로 낮춘다.
      const fit = Math.min(1, containerWidth / contentWidth);
      const next = fit * scaleFactor;
      setFitWidthScale(fit);
      setScale(next);
      setScaledHeight(content.scrollHeight * next);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    observer.observe(content);
    return () => observer.disconnect();
  }, [mini, html, scaleFactor]);

  if (isLoading) {
    return (
      <div className={cn("flex items-center justify-center", className)}>
        <Loader2 className="size-4 animate-spin text-label-assistive" />
      </div>
    );
  }

  if (mini) {
    return (
      <div
        className={cn(
          "pointer-events-none select-none overflow-hidden",
          className,
        )}
      >
        <div
          className="origin-top-left"
          style={{
            transform: `scale(${MINI_SCALE})`,
            width: `${100 / MINI_SCALE}%`,
          }}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    );
  }

  return (
    <div ref={containerRef} className={cn("overflow-auto", className)}>
      <div style={{ height: scaledHeight }}>
        <div
          ref={contentRef}
          className="origin-top-left"
          style={{
            transform: `scale(${scale})`,
            width: fitWidthScale < 1 ? `${100 / fitWidthScale}%` : undefined,
          }}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    </div>
  );
};
